<template>
  <div class="phone" :class="`phone-${step}`">
    <canvas v-show="showBoard" ref="canvas" class="phone-board"></canvas>

    <!-- 1. Who are you? -->
    <section v-if="step === 'name'" class="phone-card">
      <h1>Couch Blocks</h1>
      <p class="sub">Party <b class="code">{{ code }}</b></p>
      <label>
        Your name
        <input v-model="name" maxlength="16" autocomplete="nickname" enterkeyhint="go" @keydown.enter="join">
      </label>
      <div class="swatches" role="radiogroup" aria-label="Your colour">
        <button
          v-for="c in colours"
          :key="c.key"
          type="button"
          class="swatch"
          :class="{ on: colourKey === c.key }"
          :style="{ background: c.fill }"
          :aria-label="c.name"
          :aria-checked="colourKey === c.key"
          role="radio"
          @click="colourKey = c.key"
        ></button>
      </div>
      <CustomSwatch class="phone-custom" :value="colourKey" label="Your custom colour" @pick="colourKey = $event" />
      <button class="primary big" type="button" :disabled="!name.trim()" @click="join">Join the game</button>
      <p v-if="error" class="error">{{ error }}</p>
    </section>

    <section v-else-if="step === 'connecting'" class="phone-card">
      <p>Joining {{ code }}…</p>
    </section>

    <!-- 2. Waiting for everyone -->
    <section v-else-if="step === 'lobby'" class="phone-card">
      <p class="sub">You're in</p>
      <h1 :style="{ color: myColour.fill }">{{ myName }}</h1>
      <p>{{ seats.length }} player{{ seats.length === 1 ? '' : 's' }} so far</p>
      <p class="sub">The big screen starts the game when everyone's in.</p>
      <div class="howto">
        <p><b>Drag</b> left and right to move</p>
        <p><b>Tap</b> to turn</p>
        <p><b>Flick down</b> to drop · <b>drag down slowly</b> for a row at a time</p>
        <p>Your screen follows your piece. The big screen shows everyone.</p>
      </div>
    </section>

    <section v-else-if="step === 'gone'" class="phone-card">
      <p>The game on the big screen closed.</p>
      <button class="primary" type="button" @click="reset">Join again</button>
    </section>

    <!-- In play: a thin bar over the board -->
    <div v-if="showBoard" class="phone-bar">
      <span :style="{ color: myColour.fill }">{{ myName }}</span>
      <span>{{ (game && game.score || 0).toLocaleString() }}<small v-if="bonus > 1" class="bonus"> {{ bonus }}x</small></span>
      <button v-if="hostScreen === 'playing'" type="button" class="pause" aria-label="Pause" @click="act('start')">II</button>
    </div>
    <div v-if="hostScreen === 'paused'" class="phone-overlay">
      <h2>Paused</h2>
      <button class="primary big" type="button" @click="press('start')">Carry on</button>
      <button class="secondary" type="button" @click="menu('lobby')">Leave game</button>
    </div>
    <div v-if="hostScreen === 'over'" class="phone-overlay">
      <h2>Topped out</h2>
      <p class="final">{{ (game && game.score || 0).toLocaleString() }}</p>
      <p v-if="bonus > 1" class="bonus">Group bonus {{ bonus }}x</p>
      <p v-if="game">{{ game.lines }} line{{ game.lines === 1 ? '' : 's' }} · level {{ game.level }}</p>
      <p v-if="game" class="mine">
        <b :style="{ color: myColour.fill }">You:</b> {{ game.myCleared }} block{{ game.myCleared === 1 ? '' : 's' }} in cleared lines<template v-if="game.players.length > 1">, {{ ordinal(game.myPlace) }} of {{ game.players.length }}</template>
      </p>
      <button class="primary big" type="button" @click="menu('again')">Play again</button>
      <button class="secondary" type="button" @click="menu('lobby')">Back to lobby</button>
    </div>
    <BugReportButton v-if="step !== 'play'" :game-state="{ phone: true, step, code, seats }" />
  </div>
</template>

<script>
import { joinRoom } from '@/net/room.js';
import { unpackWindow } from '@/net/snapshot.js';
import { groupBonus } from '@/engine/game.js';
import { draw, createFx, noteEvents } from '@/render/draw.js';
import { COLOURS, colour } from '@/account/palette.js';
import BugReportButton from './BugReportButton.vue';
import CustomSwatch from './CustomSwatch.vue';

const PREFS_KEY = 'couchBlocks:phone';

function readPrefs() {
  try { return JSON.parse(window.localStorage.getItem(PREFS_KEY)) || {}; } catch { return {}; }
}

// One player's phone in party mode: it is both their controller and their
// screen. It shows a ten-column window of the (very wide) shared well that
// follows their own piece; the TV shows the whole thing.
export default {
  name: 'PhoneView',
  components: { BugReportButton, CustomSwatch },
  props: { code: { type: String, required: true } },
  data() {
    const prefs = readPrefs();
    return {
      step: 'name',
      name: prefs.name || '',
      colourKey: prefs.colour || 'coral',
      colours: COLOURS,
      error: '',
      seats: [],
      myId: null,
      hostScreen: 'lobby',
      game: null,
    };
  },
  computed: {
    mySeat() { return this.seats.findIndex((s) => s.peer === this.myId); },
    myName() { return this.seats[this.mySeat]?.name || this.name; },
    myColour() { return colour(this.seats[this.mySeat]?.colourKey || this.colourKey); },
    bonus() { return groupBonus(this.game ? this.game.players.length : 1); },
    showBoard() { return this.step === 'play' && this.game; },
  },
  mounted() {
    this.lockPageGestures();
    this.fx = createFx();
    this.frame = requestAnimationFrame(this.loop);
    window.__couchPhone = this;
  },
  beforeUnmount() {
    cancelAnimationFrame(this.frame);
    this.peer?.close();
    this.wake?.release?.().catch(() => {});
  },
  methods: {
    async join() {
      if (!this.name.trim()) return;
      this.name = this.name.trim().slice(0, 16);
      try { window.localStorage.setItem(PREFS_KEY, JSON.stringify({ name: this.name, colour: this.colourKey })); } catch { /* fine */ }
      this.step = 'connecting';
      this.error = '';
      try {
        const { peer } = await joinRoom(this.code, {
          onMessage: (_, msg) => this.fromHost(msg),
          onClosed: () => { this.step = 'gone'; },
        });
        this.peer = peer;
        peer.send({
          t: 'phone', name: this.name, colour: this.colourKey, cols: this.columnsThatFit(),
        });
        this.step = 'lobby';
        this.keepAwake();
      } catch (e) {
        this.error = e.message || "Couldn't join.";
        this.step = 'name';
      }
    },
    // A phone is tall: twenty rows set the block size, which leaves spare
    // width - so show as many columns as fit (10 to 16) rather than a
    // narrow board with empty sides.
    columnsThatFit() {
      const h = window.innerHeight - 44;
      const cell = (h - 24) / (20 + 2 + 1.5);
      return Math.max(10, Math.min(16, Math.floor((window.innerWidth - 24) / cell)));
    },
    // Matt, playing on his phone: "when I press and hold or double tap it's
    // selecting text and magnifying. We need to turn off all of those
    // gestures." So for the whole page: no text selection (except the name
    // box), no long-press menu or magnifier, no double-tap or pinch zoom,
    // no pull-to-refresh or rubber-banding. The board itself also swallows
    // every touch (attachTouch), so iOS never sees a game gesture at all.
    lockPageGestures() {
      document.documentElement.classList.add('phone-mode');
      const viewport = document.querySelector('meta[name="viewport"]');
      viewport?.setAttribute('content', 'width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no,viewport-fit=cover');
      const notInInput = (e) => !e.target?.closest?.('input, textarea');
      // iOS still pinch-zooms despite user-scalable=no; these are its
      // non-standard gesture events.
      ['gesturestart', 'gesturechange', 'gestureend'].forEach((type) => {
        document.addEventListener(type, (e) => e.preventDefault(), { passive: false });
      });
      document.addEventListener('contextmenu', (e) => { if (notInInput(e)) e.preventDefault(); });
      document.addEventListener('selectstart', (e) => { if (notInInput(e)) e.preventDefault(); });
      // Double-tap zoom on the buttons and cards outside the board.
      let lastTouchEnd = 0;
      document.addEventListener('touchend', (e) => {
        const now = Date.now();
        if (now - lastTouchEnd < 350 && notInInput(e)) e.preventDefault();
        lastTouchEnd = now;
      }, { passive: false });
    },
    reset() { this.step = 'name'; this.game = null; this.seats = []; },
    // The phone mustn't go to sleep mid-game.
    async keepAwake() {
      try { this.wake = await navigator.wakeLock?.request('screen'); } catch { /* not offered */ }
    },
    fromHost(msg) {
      if (msg.t === 'hello') this.myId = msg.you;
      if (msg.t === 'full') {
        this.peer?.close();
        this.error = 'That game is full or already started - ask for the next round.';
        this.step = 'name';
      }
      if (msg.t === 'room') {
        this.seats = msg.seats;
        if (msg.screen !== this.hostScreen) {
          if (msg.screen === 'playing' && this.hostScreen !== 'paused') this.fx = createFx();
          this.hostScreen = msg.screen;
        }
        this.step = msg.screen === 'lobby' ? 'lobby' : 'play';
        if (msg.screen === 'lobby') this.game = null;
      }
      if (msg.t === 'win') this.game = unpackWindow(msg.win);
      if (msg.t === 'events') noteEvents(this.fx, msg.events, performance.now());
    },
    ordinal(n) {
      const tens = n % 100;
      const suffix = tens >= 11 && tens <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th');
      return `${n}${suffix}`;
    },
    act(action) { this.peer?.send({ t: 'act', d: 'touch', a: [action] }); },
    press(button) { this.peer?.send({ t: 'press', d: 'touch', b: [button] }); },
    // Play again / back to lobby / leave: the big screen does it.
    menu(action) { this.peer?.send({ t: 'phone-menu', a: action }); },

    loop(now) {
      if (this.showBoard) this.paint(now);
      this.frame = requestAnimationFrame(this.loop);
    },
    paint(now) {
      const canvas = this.$refs.canvas;
      const dpr = window.devicePixelRatio || 1;
      const w = window.innerWidth;
      const h = window.innerHeight - 44; // the bar
      if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
        this.ctx = canvas.getContext('2d', { alpha: false });
        this.attachTouch(canvas);
        // Turned the phone, or the browser bar came or went.
        const cols = this.columnsThatFit();
        if (cols !== this.sentCols) {
          this.sentCols = cols;
          this.peer?.send({ t: 'phone-cols', cols });
        }
      }
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const me = this.mySeat;
      const L = draw(this.ctx, this.game, this.fx, {
        cssW: w,
        cssH: h,
        now,
        labels: this.seats.map((s, i) => (i === me ? 'You' : s.name)),
        colours: this.seats.map((s) => colour(s.colourKey)),
        nextOnly: me,
        tags: true,
        nextRows: 2,
      });
      this.cellPx = L.cell;
    },

    // ---- touch ---------------------------------------------------------------
    // Drag sideways: one cell per cell-width dragged, so the piece stays
    // under your finger. Tap: turn. Quick flick down: drop. Slow drag down:
    // a row per cell dragged.
    attachTouch(canvas) {
      if (this.touchReady) return;
      this.touchReady = true;
      let start = null;
      // The board takes every touch for the game: no scroll, zoom,
      // selection, magnifier or callout can start from it.
      ['touchstart', 'touchmove', 'touchend'].forEach((type) => {
        canvas.addEventListener(type, (e) => e.preventDefault(), { passive: false });
      });
      canvas.addEventListener('pointerdown', (e) => {
        canvas.setPointerCapture(e.pointerId);
        start = {
          x: e.clientX, y: e.clientY, t: performance.now(), stepX: e.clientX, stepY: e.clientY, moved: false, stepped: false,
        };
      });
      canvas.addEventListener('pointermove', (e) => {
        if (!start) return;
        const cell = (this.cellPx || 30) * 0.85;
        let dx = e.clientX - start.stepX;
        while (Math.abs(dx) >= cell) {
          this.act(dx > 0 ? 'right' : 'left');
          start.stepX += dx > 0 ? cell : -cell;
          dx = e.clientX - start.stepX;
          start.moved = true;
        }
        // A slow drag down steps the piece down a row per cell dragged -
        // the same one-row-at-a-time as tapping down on a controller.
        const slow = performance.now() - start.t > 220;
        if (!start.moved && slow) {
          let dy = e.clientY - start.stepY;
          while (dy >= cell) {
            this.act('stepDown');
            start.stepY += cell;
            start.stepped = true;
            dy = e.clientY - start.stepY;
          }
        }
      });
      const end = (e) => {
        if (!start) return;
        const dt = performance.now() - start.t;
        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        const flick = dy > 40 && dt < 260 && dy > Math.abs(dx) * 1.5;
        const tap = !start.moved && Math.abs(dx) < 12 && Math.abs(dy) < 12 && dt < 350;
        // (A slow drag down has already done its rows as it went.)
        if (flick && !start.stepped) this.act('hardDrop');
        else if (tap) this.act('rotateCW');
        start = null;
      };
      canvas.addEventListener('pointerup', end);
      canvas.addEventListener('pointercancel', end);
    },
  },
};
</script>

<style>
/* Whole-page lockdown in party mode - see lockPageGestures(). */
html.phone-mode, html.phone-mode body {
  -webkit-user-select: none; user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  overscroll-behavior: none;
}
html.phone-mode input, html.phone-mode textarea {
  -webkit-user-select: text; user-select: text; -webkit-touch-callout: default;
}
</style>

<style scoped>
.phone {
  position: fixed; inset: 0; background: #12141c; color: #eef0f6;
  touch-action: none; user-select: none; -webkit-user-select: none; -webkit-touch-callout: none;
}
.phone * { -webkit-touch-callout: none; }
.phone button { touch-action: manipulation; }
.phone-board { position: absolute; left: 0; top: 44px; display: block; touch-action: none; }
.phone-card {
  position: absolute; inset: 0; overflow-y: auto; padding: 24px 16px calc(24px + env(safe-area-inset-bottom, 0px));
  display: flex; flex-direction: column; gap: 1rem; align-items: stretch; justify-content: center; text-align: center;
  touch-action: pan-y;
}
h1 { margin: 0; font-size: 2.2rem; letter-spacing: -0.02em; }
h2 { margin: 0; font-size: 1.8rem; }
.sub { margin: 0; opacity: 0.7; }
.code { color: #ffcc3d; letter-spacing: 0.15em; }
label { display: flex; flex-direction: column; gap: 0.4rem; text-align: left; font-size: 0.9rem; opacity: 0.9; }
input {
  font: inherit; font-size: 1.3rem; font-weight: 700; background: #1a1d29; color: #eef0f6;
  border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 10px; padding: 0.7rem 0.8rem;
}
.swatches { display: grid; grid-template-columns: repeat(6, 1fr); gap: 0.6rem; justify-items: center; }
.swatch { width: 44px; height: 44px; border-radius: 50%; border: 3px solid transparent; padding: 0; }
.swatch.on { border-color: #fff; box-shadow: 0 0 0 3px #12141c inset; }
.phone-custom { --swatch: 44px; --ring: 3px; align-self: center; }
.primary { background: #ff6b5b; color: #12141c; border: 0; border-radius: 12px; padding: 0.6rem 1rem; font: inherit; font-weight: 800; }
.primary.big { font-size: 1.2rem; padding: 1rem; }
.primary:disabled { opacity: 0.4; }
.secondary {
  background: rgba(255, 255, 255, 0.1); color: #eef0f6; border: 0; border-radius: 12px;
  padding: 0.8rem 1rem; font: inherit; font-weight: 700;
}
.error { color: #ff8f84; margin: 0; }
.howto { opacity: 0.75; font-size: 0.95rem; }
.howto p { margin: 0.3rem 0; }
.phone-bar {
  position: absolute; top: 0; left: 0; right: 0; height: 44px; padding: 0 16px;
  display: flex; align-items: center; justify-content: space-between; gap: 1rem;
  font-weight: 800; font-variant-numeric: tabular-nums; background: #12141c;
}
.pause {
  background: rgba(255, 255, 255, 0.1); color: #eef0f6; border: 0; border-radius: 8px;
  width: 44px; height: 32px; font-weight: 900; letter-spacing: -0.1em;
}
.phone-overlay {
  position: absolute; inset: 44px 0 0; background: rgba(18, 20, 28, 0.85);
  display: flex; flex-direction: column; gap: 1rem; align-items: center; justify-content: center; padding: 16px;
}
.phone-overlay .primary, .phone-overlay .secondary { width: min(320px, 100%); }
.final { font-size: 3rem; font-weight: 800; margin: 0; }
.bonus { color: #ffbf3c; font-weight: 700; }
.mine { font-size: 1.05rem; }
</style>
